import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    TableSortLabel,
} from '@material-ui/core';
import i18n from 'i18n-js';

import styles from '../../styles';
import DateFilters from '../DateFIlters';
import { SkeletonBlock, TableSkeleton } from 'components/shared';
import { useFirstLoad } from 'hooks/useFirstLoad';
import TotalAmount from '../TotalAmount';
import agent from 'api/agent';
import globalStore from 'store/globalStore';
import { ApplicationState } from 'store';
import { actionCreators } from 'store/uiStore/actionCreators';
import { DateFilterType, DateHistoryData } from 'store/uiStore/interfaces';
import { useAppSelector } from 'index';

interface EmployeeTipsData {
    id: string;
    name: string;
    servedOrders: number;
    totalSalesAmount: number;
    tipsByCard: number;
    tipsByCash: number;
}

type OrderBy =
    | 'name'
    | 'servedOrders'
    | 'totalSalesAmount'
    | 'tipsByCard'
    | 'tipsByCash'
    | 'totalTips';

export default function Tips() {
    const classes = styles();
    const dispatch = useDispatch();
    const currency = useSelector(
        (state: ApplicationState) =>
            state.restaurantDetails?.restaurantInformation?.paymentDetails?.currency || 'CHF'
    );
    const { dateFilter, dateHistory, dateFrom, dateTo } = useAppSelector(
        (state) => state.ui.finance
    );
    const [employeesData, setEmployeesData] = useState<EmployeeTipsData[]>([]);
    const [orderBy, setOrderBy] = useState<OrderBy>('name');
    const [asc, setAsc] = useState(true);
    const [isTipsLoading, settleTips] = useFirstLoad();

    function getData(
        dayAgo: number,
        dateHistoryList?: DateHistoryData[],
        dateFrom?: Date,
        dateTo?: Date
    ) {
        if (!!!dateFrom && !!!dateTo) {
            dateFrom = new Date();
            dateTo = new Date();
            dateFrom.setDate(dateFrom.getDate() - dayAgo);
        }
        dispatch(globalStore.actionCreators.showSpiner());
        agent.Restaurants.GetTipsData(dateFrom, dateTo)
            .then((data) => {
                setEmployeesData(data);
            })
            .catch((error) => {
                dispatch(globalStore.actionCreators.showToaster('error', error.message));
            })
            .finally(() => {
                dispatch(globalStore.actionCreators.hideSpiner());
                settleTips();
            });

        dateHistoryList && setSelectedDatesHistory(dateHistoryList);
    }

    function handleOrder(field: OrderBy) {
        const isAsc = orderBy === field && asc;
        setOrderBy(field);
        setAsc(!isAsc);
    }

    function getSortedData() {
        return [...employeesData].sort((a, b) => {
            let aValue: number | string;
            let bValue: number | string;

            switch (orderBy) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'servedOrders':
                    aValue = a.servedOrders;
                    bValue = b.servedOrders;
                    break;
                case 'totalSalesAmount':
                    aValue = a.totalSalesAmount;
                    bValue = b.totalSalesAmount;
                    break;
                case 'tipsByCard':
                    aValue = a.tipsByCard;
                    bValue = b.tipsByCard;
                    break;
                case 'tipsByCash':
                    aValue = a.tipsByCash;
                    bValue = b.tipsByCash;
                    break;
                case 'totalTips':
                    aValue = a.tipsByCard + a.tipsByCash;
                    bValue = b.tipsByCard + b.tipsByCash;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return asc ? -1 : 1;
            }
            if (aValue > bValue) {
                return asc ? 1 : -1;
            }
            return 0;
        });
    }

    function setDateFilter(filter: DateFilterType) {
        dispatch(actionCreators.setFinanceDateFilter(filter));
    }

    function setSelectedDatesHistory(dateHistory: DateHistoryData[]) {
        dispatch(actionCreators.setFinanceDateHistory(dateHistory));
    }

    function handleDateRangeChange(from: Date | null, to: Date | null): void {
        dispatch(actionCreators.setFinanceDateRange(from, to));
    }

    return (
        <div style={{ position: 'relative' }}>
            <div className={classes.topContainer} style={{ justifyContent: 'flex-end' }}>
                <TotalAmount
                    style={{ position: 'static' }}
                    currency={currency}
                    title={i18n.t('common.total')}
                    amount={employeesData
                        .reduce((sum, waiter) => sum + waiter.tipsByCard + waiter.tipsByCash, 0)
                        .toFixed(2)}
                />
            </div>

            <div className={`${classes.flexRowEnd} ${classes.p15}`}>
                <div className={classes.flexRowSpaceBetween} style={{ width: '100%' }}>
                    <DateFilters
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        dateFilter={dateFilter}
                        setDateFilter={setDateFilter}
                        getData={getData}
                        selectedDatesHistory={dateHistory}
                        setSelectedDatesHistory={setSelectedDatesHistory}
                        onDateRangeChange={handleDateRangeChange}
                        onDataError={settleTips}
                    />
                </div>
            </div>

            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table aria-label="waiters tips table" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sortDirection={asc ? 'asc' : 'desc'}
                                className={classes.tableHeader}
                            >
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('name')}
                                >
                                    {i18n.t('roles.Waiter')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                sortDirection={asc ? 'asc' : 'desc'}
                                className={classes.tableHeader}
                            >
                                <TableSortLabel
                                    active={orderBy === 'servedOrders'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('servedOrders')}
                                >
                                    {i18n.t('common.numberOfServedOrders')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                sortDirection={asc ? 'asc' : 'desc'}
                                className={classes.tableHeader}
                            >
                                <TableSortLabel
                                    active={orderBy === 'totalSalesAmount'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('totalSalesAmount')}
                                >
                                    {i18n.t('common.totalSalesAmount')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                sortDirection={asc ? 'asc' : 'desc'}
                                className={classes.tableHeader}
                            >
                                <TableSortLabel
                                    active={orderBy === 'tipsByCard'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('tipsByCard')}
                                >
                                    {i18n.t('common.tipsByCard')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                sortDirection={asc ? 'asc' : 'desc'}
                                className={classes.tableHeader}
                            >
                                <TableSortLabel
                                    active={orderBy === 'tipsByCash'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('tipsByCash')}
                                >
                                    {i18n.t('common.tipsByCash')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                align="right"
                                style={{ paddingRight: 12, paddingBottom: 16 }}
                                className={classes.tableHeader}
                                sortDirection={asc ? 'asc' : 'desc'}
                            >
                                <TableSortLabel
                                    active={orderBy === 'totalTips'}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder('totalTips')}
                                >
                                    {i18n.t('common.totalTips')}
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {getSortedData().map((waiter) => (
                            <TableRow key={waiter.id}>
                                <TableCell component="th" scope="row">
                                    {waiter.name}
                                </TableCell>
                                <TableCell align="right">{waiter.servedOrders}</TableCell>
                                <TableCell align="right">
                                    {waiter.totalSalesAmount.toFixed(2)}
                                </TableCell>
                                <TableCell align="right">{waiter.tipsByCard.toFixed(2)}</TableCell>
                                <TableCell align="right">{waiter.tipsByCash.toFixed(2)}</TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                        {(waiter.tipsByCard + waiter.tipsByCash).toFixed(2)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TableSkeleton
                isLoading={isTipsLoading}
                columnWidths={[undefined, 160, 160, 160, 160, 40]}
                rowCount={8}
            >
                <SkeletonBlock height={36} width={360} />
                <SkeletonBlock height={40} width={180} style={{ marginLeft: 'auto' }} />
            </TableSkeleton>
        </div>
    );
}
